import React, { Suspense, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Snackbar,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material';
import BrowseTab from './BrowseTab';
import SearchTab from './SearchTab';
import UploadTab from './UploadTab';
import type { FileRecord, User } from '../../lib/cfos/types';
import { useTabSearchParam } from '../../hooks/useTabSearchParam';

const PdfWorkspace = React.lazy(() => import('./PdfWorkspace'));
const OfficeSessionPanel = React.lazy(() => import('./OfficeSessionPanel'));

type TabKey = 'browse' | 'search' | 'upload';

const CFOS_VIEWS: readonly TabKey[] = ['browse', 'search', 'upload'];

export interface CfosWorkspaceProps {
  user: User;
  sandboxId: string;
  /** When true, Browse imports legacy localStorage empty folders into this sandbox (Shared only). */
  allowLegacyFolderImport?: boolean;
  /** When null/undefined, Search looks across all sandboxes. */
  searchSandboxId?: string | null;
  /** Hide the inner Browse/Search/Upload tabs chrome when embedded densely. */
  compact?: boolean;
  /** Sync Browse/Search/Upload to URL `?view=` (default true). */
  syncViewToUrl?: boolean;
}

const CfosWorkspace: React.FC<CfosWorkspaceProps> = ({
  user,
  sandboxId,
  allowLegacyFolderImport = false,
  searchSandboxId,
  compact = false,
  syncViewToUrl = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const allowedViews = useMemo(() => CFOS_VIEWS, []);
  const [urlView, setUrlView] = useTabSearchParam(allowedViews, 'browse', 'view');
  const [localView, setLocalView] = useState<TabKey>('browse');
  const tab = syncViewToUrl ? urlView : localView;
  const setTab = syncViewToUrl ? setUrlView : setLocalView;
  const [viewerFile, setViewerFile] = useState<FileRecord | null>(null);
  const [viewerSandboxId, setViewerSandboxId] = useState<string>(sandboxId);
  const [officeFile, setOfficeFile] = useState<FileRecord | null>(null);
  const [officePreview, setOfficePreview] = useState(false);
  const [officeAutoEdit, setOfficeAutoEdit] = useState(false);
  const [browseNonce, setBrowseNonce] = useState(0);
  const [recent, setRecent] = useState<FileRecord[]>([]);
  const [snack, setSnack] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const panelBorder = isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)';
  const surfaceBg = isDark ? '#0f2134' : '#ffffff';
  const sand = '#ccba9b';

  const notify = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnack({ message, severity });
  }, []);

  const openViewer = useCallback((file: FileRecord) => {
    const fromActive = file.paths.find((p) => p.sandbox_id === sandboxId)?.sandbox_id;
    setViewerSandboxId(fromActive ?? file.paths[0]?.sandbox_id ?? sandboxId);
    setViewerFile(file);
    setRecent((items) => [file, ...items.filter((item) => item.id !== file.id)].slice(0, 8));
  }, [sandboxId]);

  const openOffice = useCallback((file: FileRecord, opts?: { preview?: boolean; edit?: boolean }) => {
    setOfficePreview(Boolean(opts?.preview));
    setOfficeAutoEdit(Boolean(opts?.edit));
    setOfficeFile(file);
    setRecent((items) => [file, ...items.filter((item) => item.id !== file.id)].slice(0, 8));
  }, []);

  const refreshBrowse = useCallback(() => {
    setBrowseNonce((n) => n + 1);
  }, []);

  const effectiveSearchSandbox = searchSandboxId === undefined ? sandboxId : searchSandboxId;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box
        sx={{
          border: 1,
          borderColor: panelBorder,
          borderRadius: 1,
          bgcolor: surfaceBg,
          mb: compact ? 1 : 2,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value: TabKey) => setTab(value)}
          sx={{
            borderBottom: 1,
            borderColor: panelBorder,
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 48 },
            '& .Mui-selected': { color: sand },
            '& .MuiTabs-indicator': { bgcolor: sand },
          }}
        >
          <Tab label="Browse" value="browse" />
          <Tab label="Search" value="search" />
          <Tab label="Upload" value="upload" />
        </Tabs>
      </Box>

      <Box
        sx={{
          display: tab === 'browse' ? 'flex' : 'none',
          flex: 1,
          minHeight: 0,
          flexDirection: 'column',
        }}
      >
        <BrowseTab
          user={user}
          sandboxId={sandboxId}
          allowLegacyFolderImport={allowLegacyFolderImport}
          refreshKey={browseNonce}
          syncToUrl={syncViewToUrl}
          onOpenViewer={openViewer}
          onOpenOffice={openOffice}
          notify={notify}
          panelBorder={panelBorder}
        />
      </Box>
      {tab === 'search' && (
        <SearchTab
          user={user}
          sandboxId={effectiveSearchSandbox}
          onOpenViewer={openViewer}
          onOpenOffice={openOffice}
          notify={notify}
          panelBorder={panelBorder}
        />
      )}
      {tab === 'upload' && (
        <UploadTab
          sandboxId={sandboxId}
          notify={notify}
          panelBorder={panelBorder}
        />
      )}

      {viewerFile && (
        <Suspense fallback={null}>
          <PdfWorkspace
            file={viewerFile}
            sandboxId={viewerSandboxId}
            recent={recent}
            user={user}
            onOpen={openViewer}
            onFileModified={refreshBrowse}
            onClose={() => {
              setViewerFile(null);
              refreshBrowse();
            }}
          />
        </Suspense>
      )}

      {officeFile && (
        <Suspense fallback={null}>
          <OfficeSessionPanel
            file={officeFile}
            user={user}
            panelBorder={panelBorder}
            notify={notify}
            startInPreview={officePreview}
            startInEdit={officeAutoEdit}
            onClose={() => {
              setOfficeFile(null);
              setOfficePreview(false);
              setOfficeAutoEdit(false);
              refreshBrowse();
            }}
            onFileReplaced={(next) => setOfficeFile(next)}
            onRefresh={refreshBrowse}
          />
        </Suspense>
      )}

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CfosWorkspace;
